/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useState, useContext, useEffect, useRef } from "react";
import { Banner, ResourceList } from "lib/components";
import { QuickReport } from "app/reports";
import { useQuery, useMutation } from "react-apollo";
import {
  Badge,
  Button,
  Caption,
  Card,
  FormLayout,
  Heading,
  Modal,
  ResourceItem,
  Select,
  Stack,
  Tag,
  Tabs,
  TextField,
  TextStyle,
  Thumbnail,
} from "@shopify/polaris";

import { baseHelper, imageHelper, errorHelper, productStockHelper } from "lib/helpers";
import { PrivateContext } from "lib/context";
import MoreFilters, { Filter, ShopifyStatusFilter } from "app/productOld/modules/generic/moreFilters/moreFilter";

import GET_TEMP_PRODUCT_LIST from "app/productOld/apollo/queries/getTempProductList";
import RE_SYNC_PRODUCT from "app/productOld/apollo/mutations/reSync";
import { SYNC_SHOPIFY_PRODUCT } from "app/productOld/apollo/mutations";
import TEMP_PRODUCT_SUBSCRIPTION from "app/productOld/apollo/subscriptions/tempProductList";

import constant from "lib/constant/constant";
import { withErrorBoundary, withFeature } from "lib/hoc";
import Popover from "lib/components/popover/popover";

const { SELECTED_FILTER, TAGGED_WITH, TITLE_ASC, TITLE_DESC, UPDATE_ASC, UPDATE_DESC } = constant;

const FILTER_ACTIVE = "active";
const FILTER_DRAFT = "draft";
const FILTER_ARCHIVED = "archived";

function parseShopifyListStatusFromQuery(queryParams) {
  const lf = (queryParams.list_filter && queryParams.list_filter.split(",")) || [];
  const ls = (queryParams.list_search && queryParams.list_search.split(",")) || [];
  const idx = lf.indexOf(constant.SHOPIFY_LIST_STATUS);
  if (idx < 0) return [];
  const raw = ls[idx] || "";
  if (!raw) return [];
  return raw
    .split("|")
    .map((s) => s.trim())
    .filter(Boolean);
}

function parseTitleFilterPairFromQuery(queryParams) {
  const lf = (queryParams.list_filter && queryParams.list_filter.split(",")) || [];
  const ls = (queryParams.list_search && queryParams.list_search.split(",")) || [];
  const titleIdx = lf.indexOf(constant.TITLE);
  if (titleIdx >= 0) {
    return {
      selectedFilter: constant.TITLES,
      taggedWith: ls[titleIdx] != null ? ls[titleIdx] : "",
    };
  }
  return {
    selectedFilter: null,
    taggedWith: null,
  };
}

function buildListFilterSearch({ selectedFilter, taggedWith, shopifyListStatus }) {
  const filters = [];
  const searches = [];
  const listFilterVal = selectedFilter === constant.TITLES ? constant.TITLE : selectedFilter;
  if (listFilterVal) {
    filters.push(listFilterVal);
    searches.push(taggedWith || "");
  }
  let encoded = "";
  if (Array.isArray(shopifyListStatus)) {
    encoded = shopifyListStatus.map((s) => String(s).trim()).filter(Boolean).join("|");
  } else if (shopifyListStatus && typeof shopifyListStatus === "string") {
    encoded = shopifyListStatus.trim();
  }
  if (encoded) {
    filters.push(constant.SHOPIFY_LIST_STATUS);
    searches.push(encoded);
  }
  return {
    list_filter: filters.join(","),
    list_search: searches.join(","),
  };
}

function labelOneShopifyListStatus(value, cms) {
  if (!value) return "";
  const syncLabels = {
    [constant.SYNCED]: cms("label.synced"),
    [constant.NOT_SYNCED]: cms("label.notSynced"),
  };
  if (syncLabels[value]) return syncLabels[value];
  if (value === FILTER_ACTIVE) return "Active";
  if (value === FILTER_DRAFT) return "Draft";
  if (value === "archived") return "Archived";
  if (value === "unlisted") return "Unlisted";
  if (value === constant.OUT_OF_STOCK) return cms("label.outOfStock") || "Out of stock";
  if (value === constant.IN_STOCK) return cms("label.inStock") || "In stock";
  return baseHelper.ucFirst(value);
}

const List = () => {
  const { history, cms } = useContext(PrivateContext);
  const queryParams = baseHelper.getQueryParams(history.location.search);
  const [selected, setSelected] = useState(parseInt(queryParams.tab, 10) || 0);
  let searchVal = queryParams.search && queryParams.search.split("+");
  searchVal = searchVal && searchVal.length && searchVal.join(" ");
  const [values, setValues] = useState(searchVal || "");
  const [selectedAction, setSelectedAction] = useState("");
  const [appliedFiltersValue, setAppliedFiltersValue] = useState([]);
  const [selectedButtonIndex, setSelectedButtonIndex] = useState(null);
  const [queryValue, setQueryValue] = useState(searchVal || "");
  const [selectedItems, setSelectedItems] = useState([]);
  const getQueryParams = baseHelper.getQueryParams(history.location.search);
  const [sortValue, setSortValue] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const [page, setPage] = useState(getQueryParams.page || 1);
  const [perPage, setPerPage] = useState(getQueryParams.perPage || 20);
  const [activePopover, setActivePopover] = useState({});
  const initialTitleFilter = parseTitleFilterPairFromQuery(queryParams);
  const [taggedWith, setTaggedWith] = useState(initialTitleFilter.taggedWith);
  const [selectedFilter, setSelectedFilter] = useState(initialTitleFilter.selectedFilter);
  const [dataToFetch, setDataToFetch] = useState("");
  const [statusFilter, setStatusFilter] = useState(() => {
    const { filter, status_filter: statusFilterParam } = queryParams || {};
    const effectiveFilter = filter || statusFilterParam;
    if (effectiveFilter === FILTER_ACTIVE || effectiveFilter === FILTER_DRAFT || effectiveFilter === FILTER_ARCHIVED) {
      return effectiveFilter;
    }
    return "";
  });
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [pendingSync, setPendingSync] = useState(null);
  const [banner, setBanner] = useState({
    isOpen: false,
    status: "",
    title: "",
    action: null,
  });

  const shopifyStatusRef = useRef((() => {
    const initial = parseShopifyListStatusFromQuery(queryParams);
    return initial.length ? initial.join("|") : "";
  })());

  if (!getQueryParams) {
    getQueryParams.search = "";
    getQueryParams.sort_name = "";
    getQueryParams.sort_order = "";
    getQueryParams.filter = constant.ALL;
  }
  if (selectedFilter === constant.TITLE) {
    setSelectedFilter(constant.TITLES);
  }

  const effectiveFilter = getQueryParams.filter || getQueryParams.status_filter;

  const inputData = {
    search: getQueryParams.search,
    sort_name: getQueryParams.sort_name,
    sort_order: getQueryParams.sort_order,
    filter: effectiveFilter || constant.ALL,
    list_filter: getQueryParams.list_filter,
    list_search: getQueryParams.list_search,
    page: parseInt(getQueryParams.page || page, 10),
    perPage: parseInt(getQueryParams.perPage || perPage, 10),
  };

  const { loading, data, subscribeToMore, refetch } = useQuery(GET_TEMP_PRODUCT_LIST, {
    variables: { input: inputData },
  });

  const [reSyncActions, { loading: reSyncLoading }] = useMutation(RE_SYNC_PRODUCT);

  const [bulkProductActions, { loading: loadingBulkProduct }] = useMutation(SYNC_SHOPIFY_PRODUCT, {
    refetchQueries: [
      {
        query: GET_TEMP_PRODUCT_LIST,
        variables: { input: inputData },
      },
    ],
  });

  const tempListData = baseHelper.getResponseData(data, constant.gql.GET_TEMP_PRODUCT_LIST);

  useEffect(() => {
    const paramValue = baseHelper.getQueryParams(history.location.search);
    const listFilter = (paramValue.list_filter && paramValue.list_filter.split(",")) || [];
    const listSearch = (paramValue.list_search && paramValue.list_search.split(",")) || [];
    const appliedFilters = [];
    listFilter.forEach((item, index) => {
      appliedFilters.push({
        key: item,
        value: listSearch[index],
      });
    });
    setAppliedFiltersValue(appliedFilters);
  }, [history.location.search, values]);

  useEffect(() => {
    const params = baseHelper.getQueryParams(history.location.search);
    const parsed = parseShopifyListStatusFromQuery(params);
    const hasShopify = params.list_filter && params.list_filter.split(",").includes(constant.SHOPIFY_LIST_STATUS);
    if (hasShopify) {
      shopifyStatusRef.current = parsed.length ? parsed.join("|") : "";
    }
  }, [history.location.search]);

  useEffect(() => {
    const params = baseHelper.getQueryParams(history.location.search);
    const hasShopify = params.list_filter && params.list_filter.split(",").includes(constant.SHOPIFY_LIST_STATUS);
    if (shopifyStatusRef.current && !hasShopify) {
      const { list_filter: lf, list_search: ls } = buildListFilterSearch({
        selectedFilter,
        taggedWith,
        shopifyListStatus: shopifyStatusRef.current.split("|").filter(Boolean),
      });
      const search = baseHelper.setQueryParams(history.location, { list_filter: lf, list_search: ls });
      history.replace(`${history.location.pathname}?${search}`);
    }
  }, [selectedFilter, taggedWith, history.location.search]);

  useEffect(() => {
    if (subscribeToMore) {
      subscribeToMore({
        document: TEMP_PRODUCT_SUBSCRIPTION,
        updateQuery: (prev, { subscriptionData }) => {
          if (!subscriptionData.data) return prev;
          return refetch();
        },
      });
    }
  }, [subscribeToMore, refetch]);

  const handleSearchValue = (searchValue) => {
    const search = baseHelper.setQueryParams(history.location, { search: searchValue });
    setQueryValue(searchValue);
    history.push(`${history.location.pathname}?${search}`);
  };

  const handleQueryValueRemove = () => {
    const search = baseHelper.setQueryParams(history.location, { search: "" });
    setQueryValue("");
    history.push(`${history.location.pathname}?${search}`);
  };

  const handleSort = (select) => {
    const sort = select.split("_");
    if (sort && sort.length === 2) {
      const sortedValue = baseHelper.setQueryParams(history.location, { sort_name: sort[0], sort_order: sort[1] });
      setSortValue(select);
      history.push(`${history.location.pathname}?${sortedValue}`);
    }
  };

  const tabsData = [
    { id: "all", panelID: "all", content: cms("label.all"), value: constant.ALL },
    { id: "synced", panelID: "synced", content: cms("label.synced"), value: constant.SYNCED },
    { id: "unsynced", panelID: "unsynced", content: cms("label.notSynced"), value: constant.NOT_SYNCED },
    { id: "active", panelID: "active", content: "Active Products", value: FILTER_ACTIVE },
    { id: "draft", panelID: "draft", content: "Draft Products", value: FILTER_DRAFT },
    { id: "archived", panelID: "archived", content: "Archived Products", value: FILTER_ARCHIVED },
    { id: "out_of_stock", panelID: "out_of_stock", content: cms("label.outOfStock") || "Out of stock", value: constant.OUT_OF_STOCK },
  ];

  const handleTabChange = useCallback(
    (selectedTabIndex) => {
      setSelected(selectedTabIndex);
      setSelectedItems([]);
      const tab = tabsData[selectedTabIndex];
      const isStatusTab = tab.value === FILTER_ACTIVE || tab.value === FILTER_DRAFT || tab.value === FILTER_ARCHIVED;
      setStatusFilter(isStatusTab ? tab.value : "");
      const filterValue = baseHelper.setQueryParams(history.location, {
        filter: tab.value,
        tab: selectedTabIndex,
      });
      history.push(`${history.location.pathname}?${filterValue}`);
    },
    [history, tabsData]
  );

  const reSyncAll = async () => {
    const formData = {
      isResync: true,
    };
    await reSyncActions({ variables: { input: { ...formData } } }).then((res) => {
      if (res) {
        const responseData = res.data;
        const responseDataError = res.errors;
        if (responseData) {
          const title = cms("message.success.requestBackground");
          setBanner({ isOpen: true, title, status: constant.SUCCESS });
        }
        if (responseDataError) {
          setBanner({ isOpen: true, title: responseDataError, status: constant.CRITICAL });
        }
      }
    });
  };

  const handleSync = async (eventKey, id = false) => {
    let formData = {};

    formData = {
      key: eventKey,
      ids: selectedItems,
      searchParams: {
        search: searchVal,
      },
    };

    if (id) {
      formData.ids = [id];
      setListLoading(`${eventKey}_${id}`);
    }

    await bulkProductActions({ variables: { input: { ...formData } } })
      .then((res) => {
        setListLoading(false);
        if (res) {
          const responseData = baseHelper.getResponseData(res.data, constant.gql.SYNC_SHOPIFY_PRODUCT);
          const responseDataError = baseHelper.getResponseError(res.data, constant.gql.SYNC_SHOPIFY_PRODUCT);
          if (responseData) {
            const title = cms("message.success.resyncProductBackground");
            setBanner({ isOpen: true, title, status: constant.SUCCESS });
            setSelectedItems([]);
          }
          if (responseDataError) {
            setBanner({ isOpen: true, title: responseDataError, status: constant.CRITICAL });
          }
        }
      })
      .catch((exception) => {
        setListLoading(false);
        setBanner({ isOpen: true, title: errorHelper.parse(exception), status: constant.CRITICAL });
      });
  };

  const openSyncModal = (type, id) => {
    setPendingSync({ type, id });
    setSyncModalOpen(true);
  };

  const executePendingSync = () => {
    setSyncModalOpen(false);
    if (!pendingSync) return;
    const { type, id } = pendingSync;
    if (type === "resync") {
      reSyncAll();
    } else if (type === "bulk") {
      handleSync(constant.APPROVE);
    } else if (type === "single") {
      handleSync(constant.APPROVE, id);
    }
    setPendingSync(null);
  };

  const bulkActions = [
    {
      content: cms("label.syncProducts"),
      disabled: selected === 1,
      onAction: () => openSyncModal("bulk"),
    },
  ];

  const handleChange = (val, _id) => {
    setSelectedButtonIndex(_id);
    if (val === constant.SYNC) {
      openSyncModal("single", _id);
    }
  };

  const handleSelectedFilterRemove = () => {
    shopifyStatusRef.current = "";
    const search = baseHelper.setQueryParams(history.location, { list_search: "", list_filter: "" });
    setSelectedFilter("");
    setTaggedWith("");
    history.push(`${history.location.pathname}?${search}`);
  };

  const handleTaggedWithRemove = () => {
    shopifyStatusRef.current = "";
    const search = baseHelper.setQueryParams(history.location, { list_search: "", list_filter: "" });
    setTaggedWith("");

    history.push(`${history.location.pathname}?${search}`);
  };

  const handleShopifyStatusChange = (value) => {
    const next = Array.isArray(value) ? value.map((s) => String(s).trim()).filter(Boolean) : [];
    shopifyStatusRef.current = next.length ? next.join("|") : "";
    const { list_filter: lf, list_search: ls } = buildListFilterSearch({
      selectedFilter,
      taggedWith,
      shopifyListStatus: next,
    });
    const search = baseHelper.setQueryParams(history.location, { list_filter: lf, list_search: ls, page: 1 });
    history.push(`${history.location.pathname}?${search}`);
  };

  const handleShopifyStatusRemove = () => {
    shopifyStatusRef.current = "";
    const { list_filter: lf, list_search: ls } = buildListFilterSearch({
      selectedFilter,
      taggedWith,
      shopifyListStatus: "",
    });
    const search = baseHelper.setQueryParams(history.location, { list_filter: lf, list_search: ls, page: 1 });
    history.push(`${history.location.pathname}?${search}`);
  };

  const shopifyListStatusFromUrl = parseShopifyListStatusFromQuery(baseHelper.getQueryParams(history.location.search));
  const removeShopifyStatusTag = useCallback(
    (tag) => () => {
      const next = shopifyListStatusFromUrl.filter((status) => status !== tag);
      shopifyStatusRef.current = next.length ? next.join("|") : "";
      const { list_filter: lf, list_search: ls } = buildListFilterSearch({
        selectedFilter,
        taggedWith,
        shopifyListStatus: next,
      });
      const search = baseHelper.setQueryParams(history.location, { list_filter: lf, list_search: ls, page: 1 });
      history.push(`${history.location.pathname}?${search}`);
    },
    [history, selectedFilter, shopifyListStatusFromUrl, taggedWith]
  );

  let shopifyStatusTagMarkup = [];
  if (shopifyListStatusFromUrl && shopifyListStatusFromUrl.length) {
    shopifyStatusTagMarkup =
      shopifyListStatusFromUrl &&
      shopifyListStatusFromUrl.map((option) => (
        <Tag key={option} onRemove={removeShopifyStatusTag(option)}>
          {labelOneShopifyListStatus(option, cms)}
        </Tag>
      ));
  }

  const disambiguateLabel = (key, value) => {
    switch (key) {
      case TAGGED_WITH:
        return `${value}`;
      case constant.FILTER_BY_SHOPIFY_STATUS:
        return <div className="remove_tag">{shopifyStatusTagMarkup}</div>;
      default:
        return value;
    }
  };

  const isEmpty = (value) => {
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    return value === "" || value == null;
  };

  const appliedFilters = [];
  if (!isEmpty(taggedWith)) {
    appliedFilters.push({
      key: TAGGED_WITH,
      label: disambiguateLabel(TAGGED_WITH, taggedWith),
      onRemove: handleTaggedWithRemove,
    });
  }

  if (!isEmpty(selectedFilter)) {
    appliedFilters.push({
      key: SELECTED_FILTER,
      label: disambiguateLabel(SELECTED_FILTER, selectedFilter),
      onRemove: handleSelectedFilterRemove,
    });
  }

  if (!isEmpty(shopifyListStatusFromUrl)) {
    appliedFilters.push({
      key: constant.FILTER_BY_SHOPIFY_STATUS,
      label: disambiguateLabel(constant.FILTER_BY_SHOPIFY_STATUS, shopifyListStatusFromUrl),
      onRemove: handleShopifyStatusRemove,
    });
  }

  const handleClearAll = () => {
    shopifyStatusRef.current = "";
    handleTaggedWithRemove();
    handleSelectedFilterRemove();
  };

  const filters = [
    {
      key: SELECTED_FILTER,
      label: cms("label.filterBy"),
      filter: (
        <Filter
          dataToFetch={dataToFetch}
          setDataToFetch={setDataToFetch}
          taggedWith={taggedWith}
          setTaggedWith={setTaggedWith}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          isLiveConnect
        />
      ),
      shortcut: false,
    },
  ];

  if (selectedFilter) {
    filters.push({
      key: TAGGED_WITH,
      label: cms("label.filter"),
      filter: (
        <MoreFilters
          dataToFetch={dataToFetch}
          setDataToFetch={setDataToFetch}
          taggedWith={taggedWith}
          setTaggedWith={setTaggedWith}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
        />
      ),
      shortcut: false,
    });
  }

  filters.push({
    key: constant.FILTER_BY_SHOPIFY_STATUS,
    label: cms("common.label.filterByShopifyStatus") || "Filter By Shopify Status",
    filter: (
      <ShopifyStatusFilter selected={shopifyListStatusFromUrl} onChange={handleShopifyStatusChange} />
    ),
    shortcut: false,
  });

  const getStoreStatusBadge = (item) => {
    const raw = (item && item.status) || "";
    const normalized = raw && typeof raw === "string" ? raw.toLowerCase() : "";

    if (normalized === FILTER_ACTIVE) {
      return { type: constant.SUCCESS, label: "Active" };
    }
    if (normalized === FILTER_DRAFT) {
      return { type: constant.ATTENTION, label: "Draft" };
    }
    if (normalized === FILTER_ARCHIVED) {
      return { type: constant.WARNING, label: "Archived" };
    }
    if(normalized === "unlisted") {
      return { type: constant.ATTENTION, label: "Unlisted" };
    }
    return null;
  };

  const getSyncStatusBadge = (item) => {
    const { published } = item || {};
    const syncedStatusType = (published && constant.SYNCED) || constant.NOT_SYNCED;
    const syncedStatus = (published && cms("label.synced")) || cms("label.notSynced");
    return { type: syncedStatusType, label: baseHelper.ucFirst(syncedStatus) };
  };

  const getSyncModalMessage = () => {
    if (statusFilter === FILTER_DRAFT) {
      return "Are you sure you want to sync the selected draft products to the main catalogue?";
    }
    if (statusFilter === FILTER_ARCHIVED) {
      return "Are you sure you want to sync the selected archived products to the main catalogue?";
    }
    return "Are you sure you want to sync the selected products to the main catalogue?";
  };

  return (
    <>
      {banner.isOpen && (
        <>
          <Banner
            isOpen={banner.isOpen}
            status={banner.status}
            title={banner.title}
            action={banner.action}
            onDismiss={() => setBanner({ isOpen: false })}
          />
          <br />
        </>
      )}
      <Banner title={cms("banner.addProductInstruction")} isOpen status={constant.INFO} />
      <br />
      <Banner title={cms("banner.importedProductInstruction")} isOpen />
      <br />
      <QuickReport />
      <Button primary onClick={() => openSyncModal("resync")}>
        {cms("label.resyncAllProduct")}
      </Button>
      <br />
      <br />

      <Card>
        <Tabs tabs={tabsData} selected={selected} onSelect={handleTabChange} />
        <ResourceList
          resourceName={{
            singular: baseHelper.ucFirst(constant.PRODUCT),
            plural: baseHelper.ucFirst(constant.PRODUCTS),
          }}
          items={tempListData.tempProductList}
          idForItem={(item) => {
            const { _id } = item;
            return _id;
          }}
          selectable
          bulkActions={bulkActions}
          selectedItems={selectedItems}
          onSelectionChange={setSelectedItems}
          loading={loading}
          sortValue={sortValue}
          sortOptions={[
            { label: cms("label.dateDesc"), value: UPDATE_DESC },
            { label: cms("label.dateAsc"), value: UPDATE_ASC },
            { label: cms("label.productAsc"), value: TITLE_ASC },
            { label: cms("label.productDesc"), value: TITLE_DESC },
          ]}
          filters={filters}
          appliedFilters={appliedFilters}
          handleClearAll={handleClearAll}
          onSortChange={handleSort}
          onQueryChange={handleSearchValue}
          onQueryClear={handleQueryValueRemove}
          queryValue={queryValue}
          count={tempListData.count}
          page={page}
          perPage={perPage}
          setPage={setPage}
          setPerPage={setPerPage}
          renderItem={(item) => {
            const { _id, updatedAt, title, images = [], vendor } = item;
            const thumbnailImage =
              images && images.length > 0
                ? imageHelper.resize({ url: images[0].url, type: constant.imageTypes.THUMBNAIL })
                : constant.NOIMAGESNAP;

            const isPublished = item.published;
            return (
              <ResourceItem id={_id}>
                <Stack>
                  <Stack.Item>
                    <Thumbnail size={constant.LARGE} source={thumbnailImage} alt={`${title} image`} />
                  </Stack.Item>
                  <Stack.Item fill>
                    <Heading element="h2">
                      <TextStyle variation="strong">{title}</TextStyle>
                    </Heading>
                    <Caption>
                      {`${cms("common.label.by")} `}
                      <b>{vendor}</b>
                      {` ${cms("common.label.on")} ${baseHelper.formatDate(updatedAt)}`}
                    </Caption>
                    {(() => {
                      const storeBadge = getStoreStatusBadge(item);
                      const syncBadge = getSyncStatusBadge(item);
                      const stockBadgeLabel = productStockHelper.getStockBadgeLabel(item, cms);
                      const stockStats = productStockHelper.getVariantStockStats(item);
                      const stockBadgeStatus = stockStats.isFullyOutOfStock ? constant.CRITICAL : constant.WARNING;
                      return (
                        <Stack spacing="tight" wrap={false} alignment="center">
                          {storeBadge && (
                            <Stack.Item>
                              <Badge status={baseHelper.getBadgeType(storeBadge.type)}>
                                {storeBadge.label}
                              </Badge>
                            </Stack.Item>
                          )}
                          <Stack.Item>
                            <Badge status={baseHelper.getBadgeType(syncBadge.type)}>
                              {syncBadge.label}
                            </Badge>
                          </Stack.Item>
                          {stockBadgeLabel && (
                            <Stack.Item>
                              <Badge status={baseHelper.getBadgeType(stockBadgeStatus)}>
                                {stockBadgeLabel}
                              </Badge>
                            </Stack.Item>
                          )}
                        </Stack>
                      );
                    })()}
                  </Stack.Item>
                  <Stack.Item>
                    <Popover
                      // eslint-disable-next-line no-underscore-dangle
                      active={activePopover[_id]}
                      // eslint-disable-next-line no-underscore-dangle
                      setActive={() => setActivePopover({ [_id]: !activePopover[_id] })}
                      loading={_id === selectedButtonIndex && listLoading}
                      options={[
                        {
                          content: cms("label.publish"),
                          onAction: () => handleChange(constant.SYNC, _id),
                          disabled: isPublished,
                        },
                      ]}
                    />
                  </Stack.Item>
                </Stack>
              </ResourceItem>
            );
          }}
        />
      </Card>

      <Modal
        open={syncModalOpen}
        onClose={() => setSyncModalOpen(false)}
        title="Confirm Sync"
        primaryAction={{
          content: "Confirm",
          onAction: executePendingSync,
          loading: reSyncLoading || loadingBulkProduct,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: () => setSyncModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <p>{getSyncModalMessage()}</p>
        </Modal.Section>
      </Modal>
    </>
  );
};
export default withFeature(withErrorBoundary(List), {
  feature: constant.SHOPIFY_PRODUCT,
});
